import React, { useState, useEffect, useCallback } from "react";
import { Map, GoogleApiWrapper, Marker, InfoWindow } from "google-maps-react";
import styled from "styled-components";
import API from "../../network/dashboardAPI";

const MapContainer = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  margin-top: 10px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
`;

const PickupPointInfo = styled.div`
  padding: 10px;
  max-width: 250px;
`;

const PickupPointName = styled.h4`
  margin: 0 0 5px 0;
  font-size: 14px;
  font-weight: 600;
`;

const PickupPointAddress = styled.p`
  margin: 0 0 5px 0;
  font-size: 12px;
  color: #666;
`;

const PickupPointHours = styled.p`
  margin: 0 0 5px 0;
  font-size: 12px;
  color: #666;
`;

const PickupPointCompany = styled.p`
  margin: 0;
  font-size: 12px;
  color: #007bff;
  font-weight: 500;
`;

const CoolStorageBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  background-color: #00bcd4;
  color: white;
  border-radius: 4px;
  font-size: 10px;
  margin-top: 5px;
`;

const SelectButton = styled.button`
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #9cc54b;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background-color: #8ab53e;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const PickupPointsMap = ({
  google,
  defaultLatitude = 11.5564, // Cambodia default
  defaultLongitude = 104.9282,
  onSelectPickupPoint,
  selectedPickupPointId,
  customerLatitude,
  customerLongitude
}) => {
  const [pickupPoints, setPickupPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [mapCenter, setMapCenter] = useState({
    lat: customerLatitude || defaultLatitude,
    lng: customerLongitude || defaultLongitude
  });
  const [mapZoom, setMapZoom] = useState(customerLatitude ? 13 : 7);

  // Fetch pickup points
  useEffect(() => {
    fetchPickupPoints();
  }, []);

  // Update map center when customer location changes
  useEffect(() => {
    if (customerLatitude && customerLongitude) {
      setMapCenter({
        lat: customerLatitude,
        lng: customerLongitude
      });
      setMapZoom(13);
    }
  }, [customerLatitude, customerLongitude]);

  const fetchPickupPoints = async () => {
    try {
      setLoading(true);
      const response = await API.post("/pickup-points", {
        is_active: true,
        latitude: customerLatitude || defaultLatitude,
        longitude: customerLongitude || defaultLongitude,
        radius_km: 50 // 50km radius
      });

      if (response.data && response.data.success) {
        setPickupPoints(response.data.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    } finally {
      setLoading(false);
    }
  };

  const onMarkerClick = (props, marker, e) => {
    setSelectedPoint({
      ...props.pickupPoint,
      marker
    });
  };

  const onInfoWindowClose = () => {
    setSelectedPoint(null);
  };

  const handleSelectPickupPoint = (point) => {
    if (onSelectPickupPoint) {
      onSelectPickupPoint(point);
    }
  };

  const formatOpeningHours = (openingHours) => {
    if (!openingHours) return "Hours not available";
    
    try {
      const hours = typeof openingHours === 'string' ? JSON.parse(openingHours) : openingHours;
      if (hours.days) {
        return hours.days.map(d => `${d.day}: ${d.open} - ${d.close}`).join(", ");
      }
      return JSON.stringify(hours);
    } catch (e) {
      return "Hours not available";
    }
  };

  return (
    <MapContainer>
      {loading && (
        <LoadingOverlay>
          <span>Loading pickup points...</span>
        </LoadingOverlay>
      )}
      <Map
        google={google}
        zoom={mapZoom}
        style={{ width: "100%", height: "100%" }}
        initialCenter={mapCenter}
        center={mapCenter}
        streetViewControl={false}
        mapTypeControl={false}
        fullscreenControl={false}
      >
        {pickupPoints.map((point) => (
          <Marker
            key={point.id}
            position={{ lat: point.latitude, lng: point.longitude }}
            name={point.name}
            pickupPoint={point}
            onClick={onMarkerClick}
            icon={{
              url: point.delivery_company?.logo_url || "/icons/pickup-marker.png",
              scaledSize: new google.maps.Size(32, 32)
            }}
          />
        ))}

        {selectedPoint && (
          <InfoWindow
            marker={selectedPoint.marker}
            visible={selectedPoint != null}
            onClose={onInfoWindowClose}
          >
            <PickupPointInfo>
              <PickupPointName>{selectedPoint.name}</PickupPointName>
              <PickupPointAddress>{selectedPoint.address || "No address provided"}</PickupPointAddress>
              <PickupPointHours>{formatOpeningHours(selectedPoint.opening_hours)}</PickupPointHours>
              <PickupPointCompany>
                {selectedPoint.delivery_company?.name || "Delivery Company"}
              </PickupPointCompany>
              {selectedPoint.has_cool_storage && (
                <CoolStorageBadge>Cool Storage Available</CoolStorageBadge>
              )}
              {selectedPoint.description && (
                <p style={{ fontSize: "11px", marginTop: "5px", color: "#666" }}>
                  {selectedPoint.description}
                </p>
              )}
              <SelectButton
                onClick={() => handleSelectPickupPoint(selectedPoint)}
                disabled={selectedPickupPointId === selectedPoint.id}
              >
                {selectedPickupPointId === selectedPoint.id ? "Selected" : "Select this pickup point"}
              </SelectButton>
            </PickupPointInfo>
          </InfoWindow>
        )}
      </Map>
    </MapContainer>
  );
};

export default GoogleApiWrapper({
  apiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
  libraries: ['places'],
})(PickupPointsMap);
